import { create } from 'zustand'

import type { WarningDialogTypes } from '@/components/molecules/dialogs/WarningDialog'
import type { Part } from '@/services/schemas'
import type { Item } from '@/stores/CartStore'

export type DialogTypes = 'warning' | 'partInfo' | 'configurationPreview' | 'sendMyself' | 'expandedPorts' | 'portExpanderDisabled'
type CustomDialogTypes = 'warning' | 'partInfo' | 'configurationPreview'

export type Dialog = {
  open: boolean
}

export type WarningDialog = Dialog & {
  warningType: WarningDialogTypes | null
  incomingItem: Item | null
  itemWithSensorIncluded: Item | null
}

export type PartDetailsDialog = Dialog & {
  part: Part | null
}

export type ConfigPreviewDialog = Dialog & {
  hubId: string | null
}

export type Dialogs = Record<Exclude<DialogTypes, CustomDialogTypes>, Dialog>
  & Record<'warning', WarningDialog>
  & Record<'partInfo', PartDetailsDialog>
  & Record<'configurationPreview', ConfigPreviewDialog>

type DialogsStore = {
  dialogs: Dialogs

  setDialogs: (dialogs: Dialogs) => void
  add: (id: DialogTypes, dialog: Dialog, open?: boolean) => void
  remove: (dialogId: DialogTypes) => void
  open: (dialogId: DialogTypes) => void
  close: (dialogId: DialogTypes) => void
  toggle: (dialogId: DialogTypes) => void

  setConfigPreviewInfo: (hubId: string) => void
  setWarningInfo: (type: WarningDialogTypes, incomingItem: Item, itemWithSensorIncluded?: Item) => void
  setPartInfo: (part: Part) => void
}

const useDialogsStore = create<DialogsStore>((set) => {
  return {
    dialogs: {
      warning: { open: false, warningType: null, incomingItem: null, itemWithSensorIncluded: null },
      partInfo: { open: false, part: null },
      configurationPreview: { open: false, hubId: null },
      sendMyself: { open: false },
      expandedPorts: { open: false },
      portExpanderDisabled: { open: false },
    },

    setDialogs: (dialogs: Dialogs) => set({
      dialogs,
    }),
    add: (id, dialog, open = false) =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          [id]: { ...dialog, open },
        },
      })),

    remove: dialogId =>
      set((state) => {
        const updatedDialogs = { ...state.dialogs }
        delete updatedDialogs[dialogId]
        return { dialogs: updatedDialogs }
      }),

    open: dialogId =>
      set(state => ({
        dialogs: Object.keys(state.dialogs).reduce((acc, key) => {
          // @ts-expect-error aaa
          acc[key as DialogTypes] = {
            ...state.dialogs[key as DialogTypes],
            open: key === dialogId,
          }
          return acc
        }, {} as Dialogs),
      })),

    close: dialogId =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          [dialogId]: { ...state.dialogs[dialogId], open: false },
        },
      })),

    toggle: dialogId =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          [dialogId]: { ...state.dialogs[dialogId], open: !state.dialogs[dialogId].open },
        },
      })),

    setWarningInfo: (warningType, incomingItem, itemWithSensorIncluded) =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          warning: {
            ...state.dialogs.warning,
            warningType,
            incomingItem,
            itemWithSensorIncluded: itemWithSensorIncluded ?? null,
          },
        },
      })),

    setPartInfo: part =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          partInfo: {
            ...state.dialogs.partInfo,
            part,
          },
        },
      })),

    setConfigPreviewInfo: hubId =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          configurationPreview: {
            ...state.dialogs.configurationPreview,
            hubId,
          },
        },
      })),
  }
})

export default useDialogsStore
